import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { RefreshCw, ShoppingBag, TrendingUp, Package, AlertCircle, AlertTriangle, CheckCircle2, ExternalLink, Boxes, PackagePlus, Info, Euro, FileText, LineChart, Download, Megaphone, PiggyBank, Percent, Warehouse, History, Link2, Mail, Truck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { api, downloadFile, postDownload } from "@/lib/api";
import { usePersistedState } from "@/lib/persist";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, Legend, CartesianGrid, LabelList,
} from "recharts";

const fmtEUR = (n) => new Intl.NumberFormat("nl-NL", { style: "currency", currency: "EUR" }).format(n || 0);
const fmtDT = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString("nl-NL", { dateStyle: "short", timeStyle: "short" }); }
  catch { return iso; }
};

function KpiCard({ label, waarde, sub, icon: Icon, kleur = "text-primary", info }) {
  return (
    <div className="bg-card border border-border rounded-xl p-4">
      <div className="flex items-center gap-2 text-xs uppercase tracking-wide text-muted-foreground">
        {Icon && <Icon className={`h-3.5 w-3.5 ${kleur}`} />} <span>{label}</span>
        {info && (
          <span
            tabIndex={0}
            className="group relative inline-flex items-center cursor-help"
            data-testid={`kpi-info-${(label||'').toLowerCase().replace(/\s+/g,'-')}`}
          >
            <Info className="h-3 w-3 text-muted-foreground/70 hover:text-foreground" />
            <span
              role="tooltip"
              className="pointer-events-none absolute left-1/2 -translate-x-1/2 top-full mt-1.5 z-30 hidden group-hover:block group-focus-within:block w-64 rounded-md border border-border bg-popover px-3 py-2 text-[11px] leading-relaxed text-foreground shadow-lg normal-case tracking-normal"
            >
              {info}
            </span>
          </span>
        )}
      </div>
      <div className="font-display text-2xl font-medium tabular-nums mt-1">{waarde}</div>
      {sub && <div className="text-xs text-muted-foreground mt-0.5">{sub}</div>}
    </div>
  );
}

function Sectie({ titel, children, actie }) {
  return (
    <div className="bg-card border border-border rounded-xl overflow-hidden">
      <div className="flex items-center justify-between gap-3 p-4 border-b border-border">
        <div className="text-sm font-medium min-w-0">{titel}</div>
        <div className="shrink-0">{actie}</div>
      </div>
      <div className="overflow-x-auto">{children}</div>
    </div>
  );
}

// Kleuren voor gestapeld staafdiagram (kostenlagen + marge)
const DAG_KLEUREN = {
  inkoop: "#64748b",     // slate-500
  commissie: "#f59e0b",  // amber-500
  ads: "#f43f5e",        // rose-500
  lvb: "#f97316",        // orange-500
  marge: "#10b981",      // emerald-500 (positief)
};

// Compact staafdiagram voor het Dashboard-tab — toont aantal verkopen (transacties)
// per dag met daarboven het aantal (LabelList). Tooltip toont ook omzet.
function VerkopenPerDagChart({ data, periode, onPeriodeChange }) {
  const rijen = (data?.rijen || []).map((r) => ({ ...r, kort: r.datum.slice(5) }));
  const heeftData = rijen.some((r) => r.transacties > 0);
  const totaal = rijen.reduce((s, r) => s + (r.transacties || 0), 0);
  const gemDagelijks = rijen.length ? (totaal / rijen.length) : 0;
  const maxRij = rijen.reduce((best, r) => (r.transacties > (best?.transacties || 0) ? r : best), null);
  const fmtE = (n) => (n == null ? "—" : `€ ${Number(n).toLocaleString("nl-NL", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);
  const fmtDag = (d) => {
    try { return new Date(d + "T00:00:00").toLocaleDateString("nl-NL", { weekday: "short", day: "2-digit", month: "2-digit" }); }
    catch { return d; }
  };
  const omzetTotaal = rijen.reduce((s, r) => s + (r.bruto || 0), 0);

  const CustomTooltip = ({ active, payload, label }) => {
    if (!active || !payload?.length) return null;
    const row = payload[0]?.payload || {};
    return (
      <div className="bg-popover border border-border rounded-lg p-3 text-xs shadow-lg min-w-[160px]">
        <div className="font-semibold mb-1">{label}</div>
        <div className="flex justify-between tabular-nums">
          <span>Verkopen</span><span className="font-medium">{row.transacties || 0}</span>
        </div>
        <div className="flex justify-between tabular-nums">
          <span>Bruto (excl BTW)</span><span>{fmtE(row.bruto)}</span>
        </div>
      </div>
    );
  };

  const TxnLabel = ({ x, y, width, value }) => {
    if (!value) return null;
    return (
      <text x={x + width / 2} y={y - 6} textAnchor="middle" className="fill-foreground" fontSize={11} fontWeight={600}>
        {value}
      </text>
    );
  };

  return (
    <Sectie
      titel={`Aantal verkopen per dag (${data?.start_datum || "—"} → ${data?.eind_datum || "—"})`}
      actie={
        <div className="flex items-center gap-2">
          <Label className="text-[10px] uppercase text-muted-foreground">Dagen</Label>
          <Input
            type="number" min="7" max="90" value={periode}
            onChange={(e) => onPeriodeChange(parseInt(e.target.value || "30", 10))}
            className="w-20 h-8 text-right text-sm" data-testid="bol-verkopen-periode"
          />
        </div>
      }
    >
      <div className="p-4">
        {!heeftData ? (
          <div className="p-6 text-sm text-muted-foreground text-center" data-testid="bol-verkopen-leeg">
            Nog geen verkopen in deze periode.
          </div>
        ) : (
          <>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 text-xs mb-4">
              <div className="border border-border rounded-lg p-2.5 bg-secondary/30">
                <div className="text-muted-foreground uppercase text-[10px] tracking-wide">Totaal verkopen</div>
                <div className="tabular-nums font-medium mt-0.5">{totaal}</div>
              </div>
              <div className="border border-border rounded-lg p-2.5 bg-secondary/30">
                <div className="text-muted-foreground uppercase text-[10px] tracking-wide">Gem. per dag</div>
                <div className="tabular-nums font-medium mt-0.5">{gemDagelijks.toFixed(1)}</div>
              </div>
              <div className="border border-border rounded-lg p-2.5 bg-secondary/30">
                <div className="text-muted-foreground uppercase text-[10px] tracking-wide">Piekdag</div>
                <div className="tabular-nums font-medium mt-0.5">
                  {maxRij ? `${maxRij.transacties} · ${maxRij.datum.slice(5)}` : "—"}
                </div>
              </div>
            </div>
            <div style={{ width: "100%", height: 300 }} data-testid="bol-verkopen-chart">
              <ResponsiveContainer>
                <BarChart data={rijen} margin={{ top: 22, right: 12, left: 4, bottom: 4 }}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="hsl(var(--border))" />
                  <XAxis dataKey="kort" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                  <Tooltip content={<CustomTooltip />} cursor={{ fill: "hsl(var(--secondary))", opacity: 0.4 }} />
                  <Bar dataKey="transacties" name="Verkopen" fill="#008C88" radius={[4, 4, 0, 0]}>
                    <LabelList dataKey="transacties" content={<TxnLabel />} />
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>

            {/* Dag-voor-dag tabel met exacte bedragen per dag */}
            <div className="mt-5" data-testid="bol-verkopen-dagtabel">
              <div className="text-xs uppercase tracking-wide text-muted-foreground mb-2">Dag-voor-dag overzicht</div>
              <div className="border border-border rounded-lg overflow-hidden">
                <div className="max-h-[360px] overflow-y-auto">
                  <table className="w-full text-sm">
                    <thead className="text-xs uppercase text-muted-foreground bg-secondary/40 sticky top-0 z-10">
                      <tr>
                        <th className="text-left p-3">Dag</th>
                        <th className="text-right p-3">Orders</th>
                        <th className="text-right p-3">Omzet (excl. BTW)</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-border">
                      {[...rijen].reverse().map((r) => (
                        <tr key={r.datum} data-testid={`bol-dagrij-${r.datum}`}
                          className={r.transacties > 0 ? "" : "text-muted-foreground/60"}>
                          <td className="p-3 whitespace-nowrap">{fmtDag(r.datum)}</td>
                          <td className="p-3 text-right tabular-nums">{r.transacties || 0}</td>
                          <td className="p-3 text-right tabular-nums font-medium">{fmtE(r.bruto)}</td>
                        </tr>
                      ))}
                      <tr className="bg-secondary/40 font-semibold" data-testid="bol-dagtabel-totaal">
                        <td className="p-3">Totaal</td>
                        <td className="p-3 text-right tabular-nums">{totaal}</td>
                        <td className="p-3 text-right tabular-nums">{fmtE(omzetTotaal)}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </Sectie>
  );
}

function DagOverzichtChart({ data, periode, onPeriodeChange }) {
  const rijen = (data?.rijen || []).map((r) => ({
    ...r,
    // Voor de staaf tonen we alleen positieve marge apart; verlies laten we op 0 (indicator=rode dot in tooltip)
    marge_pos: r.marge > 0 ? r.marge : 0,
    marge_neg: r.marge < 0 ? r.marge : 0,
    kort: r.datum.slice(5),
  }));
  const heeftData = rijen.some((r) => r.bruto > 0 || r.lvb > 0 || r.ads > 0);

  const totalen = data?.totalen || {};
  const fmtE = (n) => (n == null ? "—" : `€ ${Number(n).toLocaleString("nl-NL", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);

  const CustomTooltip = ({ active, payload, label }) => {
    if (!active || !payload?.length) return null;
    const row = payload[0]?.payload || {};
    const layers = [
      { key: "inkoop", label: "Inkoop", kleur: DAG_KLEUREN.inkoop },
      { key: "commissie", label: "Commissie", kleur: DAG_KLEUREN.commissie },
      { key: "ads", label: "Ads", kleur: DAG_KLEUREN.ads },
      { key: "lvb", label: "LVB", kleur: DAG_KLEUREN.lvb },
    ];
    return (
      <div className="bg-popover border border-border rounded-lg p-3 text-xs shadow-lg min-w-[200px]">
        <div className="font-semibold mb-1.5 flex items-center justify-between gap-3">
          <span>{label}</span>
          <Badge variant="secondary" className="tabular-nums">{row.transacties} txn</Badge>
        </div>
        <div className="space-y-0.5 tabular-nums">
          <div className="flex justify-between border-b border-border pb-1 mb-1">
            <span>Bruto (excl BTW)</span><span className="font-medium">{fmtE(row.bruto)}</span>
          </div>
          {layers.map((l) => (
            <div key={l.key} className="flex justify-between">
              <span className="flex items-center gap-1.5"><span className="inline-block w-2 h-2 rounded-sm" style={{ background: l.kleur }} />{l.label}</span>
              <span>{fmtE(row[l.key])}</span>
            </div>
          ))}
          <div className="flex justify-between border-t border-border pt-1 mt-1">
            <span className="flex items-center gap-1.5"><span className="inline-block w-2 h-2 rounded-sm" style={{ background: row.marge >= 0 ? DAG_KLEUREN.marge : "#dc2626" }} />Marge</span>
            <span className={`font-medium ${row.marge >= 0 ? "text-emerald-600" : "text-rose-600"}`}>{fmtE(row.marge)}</span>
          </div>
        </div>
      </div>
    );
  };

  // Custom label boven elke staaf → aantal transacties
  const TxnLabel = ({ x, y, width, value }) => {
    if (!value || value === 0) return null;
    return (
      <text x={x + width / 2} y={y - 6} textAnchor="middle" className="fill-foreground" fontSize={11} fontWeight={600}>
        {value}
      </text>
    );
  };

  return (
    <Sectie
      titel={`Dagelijkse omzet & kosten (${data?.start_datum || "—"} → ${data?.eind_datum || "—"})`}
      actie={
        <div className="flex items-center gap-2">
          <Label className="text-[10px] uppercase text-muted-foreground">Dagen</Label>
          <Input
            type="number" min="7" max="90" value={periode}
            onChange={(e) => onPeriodeChange(parseInt(e.target.value || "14", 10))}
            className="w-20 h-8 text-right text-sm" data-testid="bol-dag-overzicht-periode"
          />
        </div>
      }
    >
      <div className="p-4">
        {!heeftData ? (
          <div className="p-6 text-sm text-muted-foreground text-center" data-testid="bol-dag-overzicht-leeg">
            Nog geen data — klik "Herberekenen" om het dag-overzicht op te halen.
          </div>
        ) : (
          <>
            <div className="grid grid-cols-2 sm:grid-cols-6 gap-3 text-xs mb-4">
              <div className="border border-border rounded-lg p-2.5 bg-secondary/30">
                <div className="text-muted-foreground uppercase text-[10px] tracking-wide">Transacties</div>
                <div className="tabular-nums font-medium mt-0.5">{totalen.transacties || 0}</div>
              </div>
              <div className="border border-border rounded-lg p-2.5 bg-secondary/30">
                <div className="text-muted-foreground uppercase text-[10px] tracking-wide">Bruto (excl BTW)</div>
                <div className="tabular-nums font-medium mt-0.5">{fmtE(totalen.bruto)}</div>
              </div>
              <div className="border border-border rounded-lg p-2.5 bg-secondary/30">
                <div className="text-muted-foreground uppercase text-[10px] tracking-wide" style={{ color: DAG_KLEUREN.inkoop }}>Inkoop</div>
                <div className="tabular-nums mt-0.5">{fmtE(totalen.inkoop)}</div>
              </div>
              <div className="border border-border rounded-lg p-2.5 bg-secondary/30">
                <div className="text-muted-foreground uppercase text-[10px] tracking-wide" style={{ color: DAG_KLEUREN.commissie }}>Commissie</div>
                <div className="tabular-nums mt-0.5">{fmtE(totalen.commissie)}</div>
              </div>
              <div className="border border-border rounded-lg p-2.5 bg-secondary/30">
                <div className="text-muted-foreground uppercase text-[10px] tracking-wide" style={{ color: DAG_KLEUREN.ads }}>Ads + LVB</div>
                <div className="tabular-nums mt-0.5">{fmtE((totalen.ads || 0) + (totalen.lvb || 0))}</div>
              </div>
              <div className="border border-border rounded-lg p-2.5 bg-secondary/30">
                <div className="text-muted-foreground uppercase text-[10px] tracking-wide" style={{ color: DAG_KLEUREN.marge }}>Marge</div>
                <div className={`tabular-nums font-medium mt-0.5 ${totalen.marge >= 0 ? "text-emerald-600" : "text-rose-600"}`}>{fmtE(totalen.marge)}</div>
              </div>
            </div>

            <div style={{ width: "100%", height: 360 }} data-testid="bol-dag-overzicht-chart">
              <ResponsiveContainer>
                <BarChart data={rijen} margin={{ top: 24, right: 12, left: 8, bottom: 4 }}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="hsl(var(--border))" />
                  <XAxis dataKey="kort" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => `€${v}`} />
                  <Tooltip content={<CustomTooltip />} cursor={{ fill: "hsl(var(--secondary))", opacity: 0.4 }} />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                  <Bar dataKey="inkoop" name="Inkoop" stackId="k" fill={DAG_KLEUREN.inkoop} />
                  <Bar dataKey="commissie" name="Commissie" stackId="k" fill={DAG_KLEUREN.commissie} />
                  <Bar dataKey="ads" name="Ads" stackId="k" fill={DAG_KLEUREN.ads} />
                  <Bar dataKey="lvb" name="LVB" stackId="k" fill={DAG_KLEUREN.lvb} />
                  <Bar dataKey="marge_pos" name="Marge" stackId="k" fill={DAG_KLEUREN.marge}>
                    <LabelList dataKey="transacties" content={<TxnLabel />} />
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
            <div className="text-[11px] text-muted-foreground mt-2 flex items-center gap-2">
              <Info className="h-3 w-3" />
              Getal boven elke staaf = aantal transacties (orders) op die dag. Bruto omzet = som van alle gestapelde lagen; marge = groen segment bovenaan.
            </div>
          </>
        )}
      </div>
    </Sectie>
  );
}

export default function Bol() {
  const [health, setHealth] = useState(null);
  const [dashboard, setDashboard] = useState(null);
  const [orders, setOrders] = useState([]);
  const [lvb, setLvb] = useState([]);
  const [syncing, setSyncing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [zoekLvb, setZoekLvb] = usePersistedState("bol:zoekLvb", "");
  const [zoekOrders, setZoekOrders] = usePersistedState("bol:zoekOrders", "");
  // Aanvul-advies
  const [advies, setAdvies] = useState(null);
  const [doelDagen, setDoelDagen] = useState(21);
  const [kijkTerug, setKijkTerug] = useState(30);
  const [aanvulOpen, setAanvulOpen] = useState(false);
  // Regels die de user wil aanvullen: {ean: aantal}
  const [aanvulSelectie, setAanvulSelectie] = useState({});
  const [replenishing, setReplenishing] = useState(false);
  const [laatsteReplenishment, setLaatsteReplenishment] = useState(null);
  const [aanmeldingen, setAanmeldingen] = useState([]);
  const [aanmeldSync, setAanmeldSync] = useState(false);
  // Gegevens voor een nieuwe aanmelding (referentie, leverdatum, dozen)
  const [aanmeldForm, setAanmeldForm] = useState({ reference: "", expected_delivery_date: "", number_of_load_carriers: 1 });
  const [refCheck, setRefCheck] = useState(null);
  // Onbekende EAN's (bol-producten die geen CRM-koppeling hebben)
  const [onbekendEans, setOnbekendEans] = useState([]);
  const [koppelVoor, setKoppelVoor] = useState(null); // {ean, titel, ...}
  const [prodOpties, setProdOpties] = useState([]);
  const [koppelProdId, setKoppelProdId] = useState("");
  const [koppelNiveau, setKoppelNiveau] = useState("individueel");
  const [koppelStuks, setKoppelStuks] = useState(1);
  const [koppelVervang, setKoppelVervang] = useState("");
  const [koppelBusy, setKoppelBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [h, d, o, l, oe] = await Promise.all([
        api.get("/bol/health"),
        api.get("/bol/dashboard"),
        api.get("/bol/orders"),
        api.get("/bol/lvb-voorraad"),
        api.get("/bol/onbekende-eans"),
      ]);
      setHealth(h.data);
      setDashboard(d.data);
      setOrders(o.data || []);
      setLvb(l.data || []);
      setOnbekendEans(oe.data || []);
    } catch (e) {
      toast.error("Bol.com data ophalen mislukt");
    }
    setLoading(false);
  };

  const laadAanmeldingen = async () => {
    try {
      const { data } = await api.get("/bol/replenishments", { params: { limit: 25 } });
      setAanmeldingen(data || []);
    } catch { /* stil: paneel blijft leeg */ }
  };

  const verversAanmeldStatus = async () => {
    setAanmeldSync(true);
    try {
      const { data } = await api.post("/bol/replenishments/sync-status");
      toast.success(`${data.bijgewerkt} van ${data.gecontroleerd} aanmeldingen bijgewerkt`
        + (data.teruggeboekt ? ` · ${data.teruggeboekt} afgewezen, voorraad teruggeboekt` : ""));
      if (data.fouten?.length) toast.warning(String(data.fouten[0]).slice(0, 200));
      await Promise.all([laadAanmeldingen(), load()]);
    } catch (e) { toast.error(e?.response?.data?.detail || "Bijwerken mislukt"); }
    finally { setAanmeldSync(false); }
  };

  const laadAdvies = async () => {
    try {
      const { data } = await api.get(`/bol/aanvullen-advies?dagen_dekking=${doelDagen}&kijk_terug=${kijkTerug}`);
      setAdvies(data);
      // Alle geadviseerde aantallen als voorstel selecteren (>0)
      const sel = {};
      (data.rijen || []).forEach(r => {
        if (r.advies_stuks > 0) sel[r.ean] = r.advies_stuks;
      });
      setAanvulSelectie(sel);
    } catch (e) {
      toast.error("Advies laden mislukt");
    }
  };
  useEffect(() => { load(); laadVerkopenPerDag(); laadAanmeldingen(); /* eslint-disable-next-line */ }, []);
  useEffect(() => { if (health?.ok) laadAdvies(); /* eslint-disable-next-line */ }, [health?.ok, doelDagen, kijkTerug]);

  const openKoppelDialog = async (row) => {
    setKoppelVoor(row);
    setKoppelProdId("");
    setKoppelNiveau("individueel");
    setKoppelStuks(1);
    setKoppelVervang("");
    if (prodOpties.length === 0) {
      try {
        const { data } = await api.get("/producten?limit=1000");
        setProdOpties((data || []).map(p => ({ id: p.id, naam: p.naam, sku: p.sku,
          bol_koppelingen: p.bol_koppelingen || [] })));
      } catch { toast.error("Producten laden mislukt"); }
    }
  };

  const doeKoppel = async () => {
    if (!koppelVoor || !koppelProdId) { toast.error("Kies een product"); return; }
    setKoppelBusy(true);
    try {
      await api.post("/bol/koppel-ean", {
        ean: koppelVoor.ean, product_id: koppelProdId,
        niveau: koppelNiveau, aantal_stuks: koppelStuks,
        titel: koppelVoor.titel, vervang_ean: koppelVervang || null,
      });
      toast.success(koppelVervang
        ? `EAN ${koppelVoor.ean} vervangt oude EAN ${koppelVervang}`
        : `EAN ${koppelVoor.ean} gekoppeld aan product`);
      setKoppelVoor(null);
      await load();
    } catch (e) {
      const raw = e?.response?.data?.detail;
      toast.error(typeof raw === "string" ? raw : (raw ? JSON.stringify(raw) : "Koppelen mislukt"));
    } finally { setKoppelBusy(false); }
  };

  const sync = async () => {
    setSyncing(true);
    try {
      const { data } = await api.post("/bol/sync");
      const parts = [];
      if (data.orders_nieuw) parts.push(`${data.orders_nieuw} nieuwe order(s)`);
      if (data.orders_bijgewerkt) parts.push(`${data.orders_bijgewerkt} bijgewerkt`);
      if (data.lvb_items) parts.push(`${data.lvb_items} LVB-items`);
      if (data.afgeboekt?.length) parts.push(`${data.afgeboekt.length} afgeboekt van warehouse`);
      toast.success(`Sync klaar in ${data.duurt_sec}s — ${parts.join(", ") || "geen wijzigingen"}`);
      if (data.fouten?.length) {
        toast.warning(`${data.fouten.length} waarschuwing(en) — kijk in het log op deze pagina`, { duration: 8000 });
      }
      await load();
      laadVerkopenPerDag();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Sync mislukt");
    }
    setSyncing(false);
  };

  const volledigeSyncNu = async () => {
    try {
      await api.post("/bol/auto-sync/nu");
      toast.success("Volledige sync (orders + LVB + facturen + advertenties) gestart op de achtergrond. Dit kan enkele minuten duren.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Starten mislukt");
    }
  };

  // Financieel state
  const [financieel, setFinancieel] = useState(null);
  const [facturen, setFacturen] = useState(null);
  const [forecast, setForecast] = useState(null);
  const [performance, setPerformance] = useState(null);
  const [finLoading, setFinLoading] = useState(false);
  const [finPeriode, setFinPeriode] = useState(30);
  // Dag-overzicht (gestapeld staafdiagram) — Financieel-tab (14d default)
  const [dagOverzicht, setDagOverzicht] = useState(null);
  const [dagPeriode, setDagPeriode] = useState(14);
  // Verkopen-per-dag — Dashboard-tab (30d default)
  const [verkopenPerDag, setVerkopenPerDag] = useState(null);
  const [verkopenPeriode, setVerkopenPeriode] = useState(30);

  const laadDagOverzicht = async (aantalDagen = dagPeriode) => {
    try {
      const { data } = await api.get(`/bol/dag-overzicht?dagen=${aantalDagen}`);
      setDagOverzicht(data);
    } catch (e) {
      toast.error("Dag-overzicht laden mislukt");
    }
  };

  const laadVerkopenPerDag = async (aantalDagen = verkopenPeriode) => {
    try {
      const { data } = await api.get(`/bol/dag-overzicht?dagen=${aantalDagen}`);
      setVerkopenPerDag(data);
    } catch {
      // stil — grafiek toont fallback
    }
  };

  const laadFinancieel = async () => {
    setFinLoading(true);
    try {
      const [fi, fa] = await Promise.all([
        api.get(`/bol/financieel?dagen=${finPeriode}`),
        api.get(`/bol/facturen?dagen=28`),
      ]);
      setFinancieel(fi.data);
      setFacturen(fa.data);
      laadAdsHistStatus();
      laadDagOverzicht();
    } catch (e) {
      toast.error("Financiële data laden mislukt");
    }
    setFinLoading(false);
  };

  const laadForecast = async () => {
    try {
      const { data } = await api.get("/bol/forecast?weeks_ahead=13");
      setForecast(data);
    } catch { toast.error("Forecast laden mislukt"); }
  };

  const laadPerformance = async () => {
    try {
      const { data } = await api.get("/bol/performance?name=PRODUCT_VISITS&number_of_periods=30");
      setPerformance(data);
    } catch { toast.error("Performance laden mislukt"); }
  };

  const [adsHistLoading, setAdsHistLoading] = useState(false);
  const [adsHistStatus, setAdsHistStatus] = useState(null);

  const laadAdsHistStatus = async () => {
    try {
      const { data } = await api.get("/bol/ads/historisch-status");
      setAdsHistStatus(data);
    } catch { /* stil */ }
  };

  const startAdsHistSync = async () => {
    if (!window.confirm(`Historische advertentiekosten synchroniseren over ${finPeriode} dagen? Dit kan enkele minuten duren (Bol genereert een bulk-report per 35d-chunk).`)) return;
    setAdsHistLoading(true);
    try {
      const { data } = await api.post(`/bol/ads/historisch-sync?dagen=${finPeriode}`);
      toast.success(`Historische ads gesynct: ${data.rijen_opgeslagen} rijen · ${data.unieke_eans} EAN's · ${data.chunks_uitgevoerd}/${data.chunks_totaal} chunks OK`);
      if (data.fouten?.length) toast.error(`${data.fouten.length} chunks faalden`);
      await Promise.all([laadFinancieel(), laadAdsHistStatus()]);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Historische ads-sync mislukt");
    }
    setAdsHistLoading(false);
  };

  // ---- Auto-optimalisatie van bol-bids ----
  const [bidPreview, setBidPreview] = useState(null);   // dry-run response
  const [bidBezig, setBidBezig] = useState(false);
  const [bidPreviewOpen, setBidPreviewOpen] = useState(false);

  const laadBidPreview = async () => {
    setBidBezig(true);
    try {
      const { data } = await api.post("/bol/ads/optimize-bids", {
        dagen: finPeriode, dry_run: true, min_clicks: 10,
      });
      setBidPreview(data);
      setBidPreviewOpen(true);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Bid-preview mislukt");
    }
    setBidBezig(false);
  };

  const pasBidsToe = async () => {
    if (!bidPreview) return;
    const totaal = (bidPreview.keywords_update || 0) + (bidPreview.target_products_update || 0);
    if (!window.confirm(`${totaal} bids nu daadwerkelijk aanpassen bij bol.com?\n\nBesparing per klik: € ${Math.abs(bidPreview.besparing_per_klik).toFixed(2)}\nVerhoging per klik: € ${bidPreview.verhoging_per_klik.toFixed(2)}\n\nDit is een LIVE-actie en kan niet ongedaan worden gemaakt.`)) return;
    setBidBezig(true);
    try {
      const { data } = await api.post("/bol/ads/optimize-bids", {
        dagen: finPeriode, dry_run: false, min_clicks: 10,
      });
      const totOK = (data.keywords_succes || 0) + (data.target_products_succes || 0);
      const totErr = (data.keywords_fouten?.length || 0) + (data.target_products_fouten?.length || 0);
      toast.success(`${totOK} bids bijgewerkt bij bol.com${totErr ? ` (${totErr} fouten)` : ""}`);
      setBidPreview(data);
      setBidPreviewOpen(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Bids toepassen mislukt");
    }
    setBidBezig(false);
  };

  const downloadFactuur = async (invoiceId) => {
    try {
      const { data } = await api.get(`/bol/facturen/${invoiceId}/pdf`);
      const bin = atob(data.pdf_base64);
      const bytes = new Uint8Array(bin.length);
      for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
      const blob = new Blob([bytes], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = data.bestandsnaam;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(`Factuur ${invoiceId} gedownload`);
    } catch { toast.error("PDF-download mislukt"); }
  };

  const setSel = (ean, val) => {
    const n = parseInt(val, 10);
    setAanvulSelectie(prev => ({ ...prev, [ean]: isNaN(n) || n < 0 ? 0 : n }));
  };
  const geselecteerdRegels = () => (advies?.rijen || [])
    .filter(r => (aanvulSelectie[r.ean] || 0) > 0)
    .map(r => ({ ean: r.ean, aantal: aanvulSelectie[r.ean], titel: r.titel }));
  const totaalStuks = geselecteerdRegels().reduce((s, r) => s + r.aantal, 0);

  const startReplenishment = async () => {
    const regels = geselecteerdRegels();
    if (!regels.length) { toast.error("Selecteer minstens één regel met een aantal > 0"); return; }
    setRefCheck(null);
    try {
      const { data } = await api.get("/bol/replenishments/voorstel");
      setAanmeldForm({
        reference: data.reference || "",
        expected_delivery_date: data.expected_delivery_date || "",
        number_of_load_carriers: data.number_of_load_carriers || 1,
      });
    } catch { /* voorstel is optioneel */ }
    setAanvulOpen(true);
  };

  const checkReferentie = async (ref) => {
    if (!ref?.trim()) { setRefCheck(null); return; }
    try {
      const { data } = await api.get("/bol/replenishments/check-referentie", { params: { ref } });
      setRefCheck(data);
      if (data.referentie !== ref) setAanmeldForm(f => ({ ...f, reference: data.referentie }));
    } catch { setRefCheck(null); }
  };

  const bevestigReplenishment = async () => {
    const regels = geselecteerdRegels().map(r => ({ ean: r.ean, aantal: r.aantal }));
    if (!regels.length) return;
    if (refCheck && !refCheck.vrij) { toast.error("Deze referentie bestaat al — kies een andere"); return; }
    if (!aanmeldForm.expected_delivery_date) { toast.error("Vul een verwachte leverdatum in"); return; }
    setReplenishing(true);
    try {
      const { data } = await api.post("/bol/replenishment", {
        regels,
        reference: aanmeldForm.reference || undefined,
        expected_delivery_date: aanmeldForm.expected_delivery_date,
        number_of_load_carriers: Number(aanmeldForm.number_of_load_carriers) || 1,
      });
      setLaatsteReplenishment(data);
      const afgeboektStuks = (data.afgeboekt || []).reduce((s, a) => s + (a.aantal_stuks || 0), 0);
      if (data.status === "SUCCESS") {
        toast.success(
          `Aanmelding ${data.reference} staat bij bol.com (nr. ${data.replenishment_id}) — ${data.totaal_stuks} stuks bol, ${afgeboektStuks} stuks warehouse afgeboekt`,
          { duration: 7000 }
        );
      } else {
        toast.warning(
          `Aanmelding ${data.reference} is verstuurd, maar bol.com was nog niet klaar met verwerken. Klik straks op "Status bijwerken" bij de aanmeldingen.`,
          { duration: 9000 }
        );
      }
      if (data.afgeboekt_warnings?.length) {
        toast.warning(
          `Waarschuwing: ${data.afgeboekt_warnings.length} EAN(s) niet afgeboekt (geen CRM-koppeling)`,
          { duration: 8000 }
        );
      }
      setAanvulOpen(false);
      // reset selectie en herlaad advies + LVB-voorraad (voor eigen wh. update)
      setAanvulSelectie({});
      await Promise.all([laadAdvies(), load(), laadAanmeldingen()]);
    } catch (e) {
      const detail = e?.response?.data?.detail || "Replenishment mislukt";
      toast.error(String(detail).slice(0, 500));
    }
    setReplenishing(false);
  };

  const lvbFiltered = lvb.filter(r => {
    const q = zoekLvb.toLowerCase();
    return !q || (r.titel || "").toLowerCase().includes(q) || (r.ean || "").includes(q);
  });
  const ordersFiltered = orders.filter(o => {
    const q = zoekOrders.toLowerCase();
    return !q || (o.klant_naam || "").toLowerCase().includes(q) || (o.order_id || "").includes(q)
      || (o.items || []).some(it => (it.titel || "").toLowerCase().includes(q) || (it.ean || "").includes(q));
  });

  return (
    <div className="space-y-6" data-testid="bol-pagina">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="text-sm text-muted-foreground">Platform</div>
          <h1 className="font-display text-3xl sm:text-4xl font-medium tracking-tight mt-1 flex items-center gap-3">
            <ShoppingBag className="h-8 w-8 text-primary" /> Bol.com
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Consumentenverkopen via LVB (Fulfilment door bol) — voorraad-signalen, orders en dashboard.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {health && !health.ok && (
            <Badge variant="outline" className="border-amber-400 text-amber-700 bg-amber-50" data-testid="bol-status-warn">
              <AlertCircle className="h-3 w-3 mr-1" /> Verbinding niet OK
            </Badge>
          )}
          {health?.ok && (
            <Badge variant="outline" className="border-emerald-300 text-emerald-700 bg-emerald-50" data-testid="bol-status-ok">
              <CheckCircle2 className="h-3 w-3 mr-1" /> Verbonden
            </Badge>
          )}
          <div className="text-xs text-muted-foreground" data-testid="bol-sync-info">
            Laatste sync: <span className="font-medium text-foreground">{fmtDT(health?.laatste_sync?.run_op)}</span>
            {health?.laatste_sync?.bron === "automatisch" && " (auto)"}
            {health?.auto_sync?.volgende_run && (
              <span className="block">Nachtsync: <span className="font-medium text-foreground">{fmtDT(health.auto_sync.volgende_run)}</span></span>
            )}
          </div>
          <Button onClick={sync} disabled={syncing || !health?.ok} data-testid="bol-sync-btn">
            <RefreshCw className={`h-4 w-4 mr-1 ${syncing ? "animate-spin" : ""}`} /> {syncing ? "Bezig…" : "Synchroniseer"}
          </Button>
          <Button variant="outline" onClick={volledigeSyncNu} disabled={!health?.ok} data-testid="bol-auto-sync-btn"
            title="Volledige sync inclusief advertentiekosten — draait op de achtergrond">
            Volledig + ads
          </Button>
        </div>
      </div>

      {!health?.heeft_credentials && (
        <div className="p-4 rounded-lg border border-amber-400/60 bg-amber-50 text-amber-900 text-sm">
          Bol.com API-credentials ontbreken. Vul <code>BOL_CLIENT_ID</code> en <code>BOL_CLIENT_SECRET</code> in.
        </div>
      )}
      {health?.heeft_credentials && !health?.verbonden && (
        <div className="p-4 rounded-lg border border-red-400/60 bg-red-50 text-red-900 text-sm">
          Verbinding met bol.com mislukt: <span className="font-mono text-xs">{health.fout || "onbekende fout"}</span>
        </div>
      )}

      <Tabs defaultValue="dashboard" className="space-y-4">
        <TabsList className="flex h-auto flex-wrap justify-start gap-1 sm:inline-flex sm:h-9 sm:flex-nowrap sm:justify-center sm:gap-0">
          <TabsTrigger value="dashboard" data-testid="bol-tab-dashboard"><TrendingUp className="h-3.5 w-3.5 mr-1.5" /> Dashboard</TabsTrigger>
          <TabsTrigger value="orders" data-testid="bol-tab-orders"><Package className="h-3.5 w-3.5 mr-1.5" /> Orders ({orders.length})</TabsTrigger>
          <TabsTrigger value="lvb" data-testid="bol-tab-lvb"><Boxes className="h-3.5 w-3.5 mr-1.5" /> Voorraad ({lvb.length})</TabsTrigger>
          <TabsTrigger value="onbekend" data-testid="bol-tab-onbekend"
            className={onbekendEans.length > 0 ? "text-rose-700" : ""}>
            <AlertTriangle className="h-3.5 w-3.5 mr-1.5" /> Koppelingen{onbekendEans.length > 0 ? ` (${onbekendEans.length})` : ""}
          </TabsTrigger>
          <TabsTrigger value="aanvullen" data-testid="bol-tab-aanvullen"><PackagePlus className="h-3.5 w-3.5 mr-1.5" /> Aanvullen{advies?.rijen?.some(r => r.advies_stuks > 0) ? ` (${advies.rijen.filter(r => r.advies_stuks > 0).length})` : ""}</TabsTrigger>
          <TabsTrigger value="zendingen" data-testid="bol-tab-zendingen">
            <Truck className="h-3.5 w-3.5 mr-1.5" /> Zendingen{aanmeldingen.length ? ` (${aanmeldingen.length})` : ""}
            {aanmeldingen.filter(a => a.aandacht_nodig).length > 0 && (
              <span className="ml-1.5 inline-flex items-center justify-center min-w-4 h-4 px-1 rounded-full bg-amber-500 text-[10px] text-white"
                data-testid="bol-tab-zendingen-aandacht">
                {aanmeldingen.filter(a => a.aandacht_nodig).length}
              </span>
            )}
          </TabsTrigger>
          <TabsTrigger value="financieel" data-testid="bol-tab-financieel" onClick={() => { if (!financieel) laadFinancieel(); }}><Euro className="h-3.5 w-3.5 mr-1.5" /> Financieel</TabsTrigger>
        </TabsList>

        {/* ---- Dashboard ---- */}
        <TabsContent value="dashboard" className="space-y-4">
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            <KpiCard label="Vandaag" waarde={fmtEUR(dashboard?.vandaag?.omzet)} sub={`${dashboard?.vandaag?.aantal || 0} orders`} icon={TrendingUp} />
            <KpiCard label="Deze week" waarde={fmtEUR(dashboard?.week?.omzet)} sub={`${dashboard?.week?.aantal || 0} orders`} icon={TrendingUp} />
            <KpiCard label="Deze maand" waarde={fmtEUR(dashboard?.maand?.omzet)} sub={`${dashboard?.maand?.aantal || 0} orders`} icon={TrendingUp} />
          </div>

          {/* Aantal verkopen per dag */}
          <VerkopenPerDagChart
            data={verkopenPerDag}
            periode={verkopenPeriode}
            onPeriodeChange={(n) => {
              const clamped = Math.max(7, Math.min(90, isNaN(n) ? 30 : n));
              setVerkopenPeriode(clamped);
              laadVerkopenPerDag(clamped);
            }}
          />

          <Sectie titel={`Omzet per product (deze maand) — ${dashboard?.maand?.top?.length || 0}`}>
            {(dashboard?.maand?.top || []).length === 0 ? (
              <div className="p-6 text-sm text-muted-foreground text-center">Nog geen verkopen zichtbaar. Sync haalt orders van bol op.</div>
            ) : (
              <table className="w-full text-sm">
                <thead className="text-xs uppercase text-muted-foreground bg-secondary/40">
                  <tr>
                    <th className="text-right p-3 w-32">Omzet</th>
                    <th className="text-right p-3 w-40">Aandeel</th>
                    <th className="text-left p-3">Product</th>
                    <th className="text-left p-3">EAN</th>
                    <th className="text-right p-3">Stuks</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {dashboard.maand.top.map((t) => (
                    <tr key={t.ean} data-testid={`bol-top-${t.ean}`}>
                      <td className="p-3 text-right tabular-nums font-semibold">{fmtEUR(t.omzet)}</td>
                      <td className="p-3">
                        <div className="flex items-center gap-2 justify-end">
                          <div className="flex-1 h-2 bg-secondary rounded-full overflow-hidden max-w-[100px]">
                            <div className="h-full bg-primary rounded-full" style={{ width: `${Math.min(100, t.aandeel || 0)}%` }} />
                          </div>
                          <span className="tabular-nums text-xs text-muted-foreground w-10 text-right">{(t.aandeel ?? 0).toFixed(1)}%</span>
                        </div>
                      </td>
                      <td className="p-3 max-w-xs truncate">{t.titel}</td>
                      <td className="p-3 font-mono text-xs">{t.ean}</td>
                      <td className="p-3 text-right tabular-nums">{t.stuks}</td>
                    </tr>
                  ))}
                  <tr className="bg-secondary/40 font-semibold" data-testid="bol-top-totaal">
                    <td className="p-3 text-right tabular-nums">{fmtEUR(dashboard?.maand?.producten_omzet || 0)}</td>
                    <td className="p-3 text-right tabular-nums text-xs text-muted-foreground">100%</td>
                    <td className="p-3">Totaal</td>
                    <td className="p-3" />
                    <td className="p-3 text-right tabular-nums">{dashboard.maand.top.reduce((s, t) => s + (t.stuks || 0), 0)}</td>
                  </tr>
                </tbody>
              </table>
            )}
          </Sectie>

          <Sectie titel={`Lage LVB-voorraad (< 10 stuks) — ${dashboard?.laag_lvb?.length || 0}`}>
            {(dashboard?.laag_lvb || []).length === 0 ? (
              <div className="p-6 text-sm text-muted-foreground text-center">Alle LVB-items hebben ≥ 10 op voorraad. </div>
            ) : (
              <table className="w-full text-sm">
                <thead className="text-xs uppercase text-muted-foreground bg-secondary/40">
                  <tr>
                    <th className="text-left p-3">Titel</th>
                    <th className="text-left p-3">EAN</th>
                    <th className="text-right p-3">Op LVB</th>
                    <th className="text-right p-3">Eigen wh.</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {dashboard.laag_lvb.map((l) => (
                    <tr key={l.ean} data-testid={`bol-laag-${l.ean}`}>
                      <td className="p-3 max-w-md truncate">{l.titel}</td>
                      <td className="p-3 font-mono text-xs">{l.ean}</td>
                      <td className="p-3 text-right tabular-nums font-medium text-amber-700">{l.regulier}</td>
                      <td className="p-3 text-right tabular-nums">{l.eigen_warehouse ?? "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </Sectie>
        </TabsContent>

        {/* ---- Aanvullen (LVB replenishment) ---- */}
        <TabsContent value="aanvullen" className="space-y-4">
          <div className="bg-card border border-border rounded-xl p-4">
            <div className="flex items-start gap-3 flex-wrap">
              <div className="flex items-center gap-2 text-sm font-medium">
                <PackagePlus className="h-4 w-4 text-primary" /> Aanvullingsadvies
              </div>
              <div className="text-xs text-muted-foreground flex-1 min-w-[280px]">
                Berekend uit bol-verkopen van de <strong>laatste {kijkTerug} dagen</strong>. Voorstel: elke EAN aanvullen tot <strong>{doelDagen} dagen dekking</strong>.
              </div>
              <div className="flex items-end gap-2">
                <div>
                  <Label className="text-[10px] uppercase text-muted-foreground">Doel dekking</Label>
                  <Input type="number" min="1" value={doelDagen} onChange={(e) => setDoelDagen(parseInt(e.target.value || "21", 10))}
                    className="w-20 h-9 text-right" data-testid="advies-dagen" />
                </div>
                <div>
                  <Label className="text-[10px] uppercase text-muted-foreground">Kijk terug</Label>
                  <Input type="number" min="7" value={kijkTerug} onChange={(e) => setKijkTerug(parseInt(e.target.value || "30", 10))}
                    className="w-20 h-9 text-right" data-testid="advies-terug" />
                </div>
                <Button variant="secondary" size="sm" onClick={laadAdvies} data-testid="advies-refresh">
                  <RefreshCw className="h-3.5 w-3.5 mr-1" /> Herberekenen
                </Button>
              </div>
            </div>

            {advies && advies.rijen && advies.rijen.length > 0 && (
              <div className="mt-4 flex items-center justify-between gap-3 flex-wrap p-3 rounded-lg bg-secondary/50 border border-border">
                <div className="text-sm">
                  <span className="font-medium">Totaal geselecteerd: {totaalStuks} stuks</span>
                  <span className="text-muted-foreground"> over {geselecteerdRegels().length} EAN's</span>
                </div>
                <Button onClick={startReplenishment} disabled={!totaalStuks || !health?.ok} data-testid="aanvullen-start">
                  <PackagePlus className="h-4 w-4 mr-1" /> Meld aanvulling bij bol.com
                </Button>
              </div>
            )}
          </div>


          <Sectie titel={`Advies per EAN (${advies?.rijen?.length || 0})`}>
            {!advies ? (
              <div className="p-6 text-sm text-muted-foreground text-center">Advies laden…</div>
            ) : advies.rijen.length === 0 ? (
              <div className="p-6 text-sm text-muted-foreground text-center">Nog geen LVB-items. Sync eerst met bol.</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm min-w-[760px]">
                  <thead className="text-xs uppercase text-muted-foreground bg-secondary/40">
                    <tr>
                      <th className="text-left p-3">Titel / EAN</th>
                      <th className="text-right p-3">Verk. {kijkTerug}d</th>
                      <th className="text-right p-3">Gem/dag</th>
                      <th className="text-right p-3">LVB nu</th>
                      <th className="text-right p-3">Dekking</th>
                      <th className="text-right p-3">Doel</th>
                      <th className="text-right p-3">Eigen wh.</th>
                      <th className="text-right p-3 w-28">Aanvullen</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {advies.rijen.map((r) => {
                      const val = aanvulSelectie[r.ean] ?? 0;
                      const kritiek = r.advies_stuks > 0 && r.dagen_dekking_nu !== null && r.dagen_dekking_nu < 7;
                      return (
                        <tr key={r.ean} className={kritiek ? "bg-amber-50/40" : ""} data-testid={`aanvul-row-${r.ean}`}>
                          <td className="p-3">
                            <div className="max-w-sm truncate">{r.titel}</div>
                            <div className="text-xs font-mono text-muted-foreground">{r.ean}</div>
                          </td>
                          <td className="p-3 text-right tabular-nums">{r.verkocht_periode}</td>
                          <td className="p-3 text-right tabular-nums">{r.gem_daily.toFixed(2)}</td>
                          <td className="p-3 text-right tabular-nums">{r.huidig_lvb}</td>
                          <td className="p-3 text-right tabular-nums">
                            {r.dagen_dekking_nu === null ? "∞" : (
                              <span className={kritiek ? "text-amber-700 font-medium" : ""}>{r.dagen_dekking_nu}d</span>
                            )}
                          </td>
                          <td className="p-3 text-right tabular-nums text-muted-foreground">{r.doel_stock}</td>
                          <td className="p-3 text-right tabular-nums">
                            {r.eigen_warehouse === null || r.eigen_warehouse === undefined ? "—" : (
                              <span className={r.advies_stuks > 0 && r.eigen_warehouse < r.advies_stuks ? "text-amber-700 font-medium" : ""}>
                                {r.eigen_warehouse}
                              </span>
                            )}
                          </td>
                          <td className="p-3 text-right">
                            <Input type="number" min="0" step={r.aantal_stuks_per_pack || 1} value={val}
                              onChange={(e) => setSel(r.ean, e.target.value)}
                              className={`w-20 h-8 text-right ml-auto ${val > 0 ? "border-primary" : ""}`}
                              data-testid={`aanvul-aantal-${r.ean}`} />
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </Sectie>

          <div className="flex items-start gap-2 text-xs text-muted-foreground p-3 rounded-lg bg-secondary/30 border border-border/60">
            <Info className="h-4 w-4 shrink-0 mt-0.5" />
            <div>
              <strong>Zo werkt het advies:</strong> <code>gem_daily × {doelDagen} − huidig_LVB</code>. Kritieke items (&lt; 7 dagen dekking) worden geel gemarkeerd.
              Als het advies groter is dan je eigen warehouse-voorraad, kleurt de kolom "Eigen wh." rood — dan moet je eerst bijbestellen.
              Bij aanmelden krijg je een <strong>reference</strong> waaronder de zending in bol's ontvangst-portaal terugkomt.
            </div>
          </div>
        </TabsContent>

        {/* ---- LVB voorraad ---- */}
        {/* ZENDINGEN — status van de LVB-aanmeldingen bij bol.com */}
        <TabsContent value="zendingen" className="space-y-4">
          <ZendingenTab
            aanmeldingen={aanmeldingen}
            bezig={aanmeldSync}
            onVerversen={verversAanmeldStatus}
            onNaAanpassing={async () => { await Promise.all([laadAanmeldingen(), load()]); }}
          />
        </TabsContent>


        <TabsContent value="lvb" className="space-y-4">
          <div className="flex items-center gap-3 flex-wrap">
            <Input placeholder="Zoek op titel of EAN…" value={zoekLvb} onChange={(e) => setZoekLvb(e.target.value)}
              className="max-w-sm h-10" data-testid="bol-lvb-zoek" />
            <div className="text-xs text-muted-foreground">Voeg koppelingen toe in Product → Logistiek → Bol.com koppelingen om producten aan een EAN te binden.</div>
          </div>
          <Sectie titel={`LVB-voorraad (${lvbFiltered.length})`}>
            <div className="overflow-x-auto">
              <table className="w-full text-sm min-w-[720px]">
                <thead className="text-xs uppercase text-muted-foreground bg-secondary/40">
                  <tr>
                    <th className="text-left p-3">Titel</th>
                    <th className="text-left p-3">EAN</th>
                    <th className="text-left p-3">Gekoppeld CRM-product</th>
                    <th className="text-right p-3">Op LVB</th>
                    <th className="text-right p-3">Graded</th>
                    <th className="text-right p-3">Eigen wh.</th>
                    <th className="text-right p-3">Stuks/pack</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {lvbFiltered.map((r) => (
                    <tr key={r.ean} data-testid={`bol-lvb-row-${r.ean}`}>
                      <td className="p-3 max-w-sm truncate">{r.titel}</td>
                      <td className="p-3 font-mono text-xs">{r.ean}</td>
                      <td className="p-3">
                        {r.gekoppeld_product ? (
                          <Link to={`/producten/${r.gekoppeld_product_id}`} className="text-primary hover:underline inline-flex items-center gap-1">
                            {r.gekoppeld_product} <ExternalLink className="h-3 w-3" />
                          </Link>
                        ) : (
                          <span className="text-amber-700 text-xs">
                            Niet gekoppeld — koppel via een product
                          </span>
                        )}
                      </td>
                      <td className={`p-3 text-right tabular-nums font-medium ${r.regulier < 10 ? "text-amber-700" : ""}`}>{r.regulier}</td>
                      <td className="p-3 text-right tabular-nums">{r.graded}</td>
                      <td className="p-3 text-right tabular-nums">{r.eigen_warehouse ?? "—"}</td>
                      <td className="p-3 text-right tabular-nums">{r.aantal_stuks_per_pack ?? "—"}</td>
                    </tr>
                  ))}
                  {lvbFiltered.length === 0 && (
                    <tr><td colSpan={7} className="p-6 text-center text-muted-foreground text-sm">
                      {loading ? "Laden…" : "Geen LVB-items."}
                    </td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </Sectie>
        </TabsContent>

        {/* ---- Onbekende EAN's ---- */}
        <TabsContent value="onbekend" className="space-y-4">
          <div className="rounded-lg border border-rose-200 bg-rose-50 p-4 text-sm text-rose-900">
            <div className="flex items-start gap-2">
              <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
              <div>
                <div className="font-medium">EAN's op bol.com zonder CRM-koppeling</div>
                <div className="text-xs mt-1">
                  Deze EAN's staan wél in recente bol-orders of LVB-voorraad, maar zijn niet gekoppeld aan een CRM-product.
                  Dit gebeurt meestal als bol.com de EAN van een product wijzigt (nieuwe verpakking, GTIN-update). Klik op <strong>Koppel</strong> om de EAN aan een bestaand product te hangen — dan werkt de sync weer.
                </div>
              </div>
            </div>
          </div>
          <div className="flex justify-end">
            <Button variant="outline" size="sm" onClick={() => openKoppelDialog({ ean: "", titel: "", bron: "handmatig" })}
              data-testid="handmatig-ean-koppel">
              <Link2 className="h-3.5 w-3.5 mr-1" /> Handmatig EAN koppelen
            </Button>
          </div>
          <Sectie titel={`Ontkoppelde EAN's (${onbekendEans.length})`}>
            {onbekendEans.length === 0 ? (
              <div className="p-6 text-sm text-muted-foreground text-center">Alle bol-EAN's zijn gekoppeld aan een CRM-product. </div>
            ) : (
              <table className="w-full text-sm min-w-[760px]">
                <thead className="text-xs uppercase text-muted-foreground bg-secondary/40">
                  <tr>
                    <th className="text-left p-3">Bol-titel</th>
                    <th className="text-left p-3">EAN</th>
                    <th className="text-left p-3">Bron</th>
                    <th className="text-right p-3">Orders</th>
                    <th className="text-right p-3">Stuks</th>
                    <th className="text-left p-3">Laatste</th>
                    <th className="text-right p-3">Actie</th>
                  </tr>
                </thead>
                <tbody>
                  {onbekendEans.map((r) => (
                    <tr key={r.ean} className="border-t border-border" data-testid={`onbekend-ean-row-${r.ean}`}>
                      <td className="p-3 max-w-md truncate" title={r.titel}>{r.titel || <span className="text-muted-foreground italic">(zonder titel)</span>}</td>
                      <td className="p-3 font-mono text-xs">{r.ean}</td>
                      <td className="p-3">
                        <Badge variant="outline" className="text-[10px]">{r.bron === "lvb" ? "LVB" : "Order"}</Badge>
                      </td>
                      <td className="p-3 text-right tabular-nums">{r.aantal_orders || 0}</td>
                      <td className="p-3 text-right tabular-nums font-medium">{r.aantal_stuks || 0}</td>
                      <td className="p-3 text-xs text-muted-foreground">{r.laatste_datum ? r.laatste_datum.slice(0, 10) : "—"}</td>
                      <td className="p-3 text-right">
                        <Button size="sm" onClick={() => openKoppelDialog(r)} data-testid={`koppel-ean-btn-${r.ean}`}>
                          <Link2 className="h-3.5 w-3.5 mr-1" /> Koppel
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </Sectie>
        </TabsContent>

        {/* ---- Orders ---- */}
        <TabsContent value="orders" className="space-y-4">
          <Input placeholder="Zoek op klant, order-ID, product of EAN…" value={zoekOrders} onChange={(e) => setZoekOrders(e.target.value)}
            className="max-w-md h-10" data-testid="bol-orders-zoek" />
          <Sectie titel={`Orders (${ordersFiltered.length})`}>
            {ordersFiltered.length === 0 ? (
              <div className="p-8 text-sm text-muted-foreground text-center">
                {loading ? "Laden…" : "Nog geen orders binnengehaald. Klik op Synchroniseer bovenaan."}
              </div>
            ) : (
              <div className="divide-y divide-border">
                {ordersFiltered.map((o) => (
                  <div key={o.order_id} className="p-4 space-y-2" data-testid={`bol-order-${o.order_id}`}>
                    <div className="flex items-start justify-between gap-3 flex-wrap">
                      <div>
                        <div className="font-medium">{o.klant_naam || "—"}</div>
                        <div className="text-xs text-muted-foreground font-mono">{o.order_id}</div>
                        <div className="text-xs text-muted-foreground">{fmtDT(o.order_placed)}</div>
                      </div>
                      <div className="text-right">
                        <div className="font-medium tabular-nums">{fmtEUR(o.totaal)}</div>
                        {o.afgeboekt && <Badge variant="outline" className="text-[10px] mt-1">Voorraad afgeboekt</Badge>}
                      </div>
                    </div>
                    <div className="text-xs text-muted-foreground space-y-0.5">
                      {(o.items || []).map((it, idx) => (
                        <div key={`${it.ean || it.titel || "item"}-${idx}`} className="flex items-center gap-2">
                          <span className="tabular-nums shrink-0">{it.aantal}×</span>
                          <span className="truncate">{it.titel}</span>
                          <Badge variant="outline" className="text-[9px] shrink-0">{it.fulfilment || "?"}</Badge>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Sectie>
        </TabsContent>

        {/* ---- Financieel (netto omzet, commissies, bol-facturen, forecast, performance) ---- */}
        <TabsContent value="financieel" className="space-y-4">
          <div className="flex items-end gap-3 flex-wrap">
            <div>
              <Label className="text-[10px] uppercase text-muted-foreground">Periode (dagen)</Label>
              <Input type="number" min="1" value={finPeriode} onChange={(e) => setFinPeriode(parseInt(e.target.value || "30", 10))}
                className="w-24 h-9 text-right" data-testid="fin-periode" />
            </div>
            <Button variant="secondary" size="sm" onClick={laadFinancieel} disabled={finLoading} data-testid="fin-refresh">
              <RefreshCw className={`h-3.5 w-3.5 mr-1 ${finLoading ? "animate-spin" : ""}`} /> Herberekenen
            </Button>
            <Button variant="outline" size="sm" onClick={laadForecast} data-testid="fin-forecast">
              <LineChart className="h-3.5 w-3.5 mr-1" /> Forecast laden
            </Button>
            <Button variant="outline" size="sm" onClick={laadPerformance} data-testid="fin-performance">
              <TrendingUp className="h-3.5 w-3.5 mr-1" /> Performance laden
            </Button>
          </div>

          {/* KPI's */}
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-7 gap-3">
            <KpiCard label="Bruto (excl BTW)" waarde={fmtEUR(financieel?.bruto_omzet)} sub={`${finPeriode} dagen`} icon={TrendingUp} />
            <KpiCard label="Commissie" waarde={fmtEUR(financieel?.commissie_totaal)} sub="bol-tarief per EAN" icon={Euro} kleur="text-amber-600" />
            <KpiCard
              label="Advertentiekosten"
              waarde={fmtEUR(financieel?.ads_kosten_totaal)}
              sub={financieel?.ads_fout
                ? "Advertising API niet beschikbaar"
                : `${financieel?.ads_periode?.bron === "historisch" ? "cache" : "live"}${financieel?.ads_sales14d ? ` · sales € ${financieel.ads_sales14d.toLocaleString('nl-NL')}` : ""}`}
              icon={Megaphone}
              kleur="text-rose-600"
            />
            <KpiCard label="LVB-kosten" waarde={fmtEUR(financieel?.lvb_kosten_totaal)} sub={`${fmtEUR(financieel?.lvb_kosten_toegewezen)} aan EAN's`} icon={Warehouse} kleur="text-orange-600" />
            <KpiCard label="Inkoopprijs" waarde={fmtEUR(financieel?.inkoopprijs_totaal)} sub="gekoppelde producten" icon={PiggyBank} kleur="text-slate-600" />
            <KpiCard
              label="Echte marge" waarde={fmtEUR(financieel?.marge_totaal)} sub="na alle kosten"
              icon={Euro} kleur="text-emerald-600"
              info="Marge = Bruto (excl BTW) − Bol-commissie − Advertentiekosten − LVB-kosten − Inkoopprijs. Alle bedragen exclusief BTW; Bol's consumentenprijs wordt gedeeld door 1 + BTW-% (fallback 21%) voor een eerlijke vergelijking."
            />
            <KpiCard
              label="Marge %" waarde={`${financieel?.marge_pct || 0}%`} sub="marge / bruto"
              icon={Percent} kleur="text-emerald-700"
              info="Marge % = Echte marge ÷ Bruto (excl BTW) × 100. Percentage boven ~15% is doorgaans gezond; daaronder kan het lonen om ads te verminderen of de verkoopprijs te verhogen."
            />
          </div>

          {finPeriode > 30 && (
            <div className="flex flex-wrap items-center gap-2 p-3 rounded-lg bg-sky-50 border border-sky-200 text-sky-900 text-xs" data-testid="fin-hist-hint">
              <History className="h-4 w-4 shrink-0" />
              <div className="flex-1 min-w-[200px]">
                <strong>Periode &gt; 30d</strong> — advertentiekosten komen uit de historische cache. Klik "Sync historisch" om Bol een bulk-report te laten genereren (kan enkele minuten duren).
                {adsHistStatus && !adsHistStatus.leeg && (
                  <span className="ml-2 text-sky-700">Cache: <span className="font-mono">{adsHistStatus.min_datum}</span> → <span className="font-mono">{adsHistStatus.max_datum}</span> ({adsHistStatus.aantal_rijen} rijen · {fmtEUR(adsHistStatus.totaal_kosten)})</span>
                )}
              </div>
              <Button size="sm" variant="outline" onClick={startAdsHistSync} disabled={adsHistLoading} data-testid="fin-ads-hist-sync" className="border-sky-300 text-sky-900 hover:bg-sky-100">
                <RefreshCw className={`h-3.5 w-3.5 mr-1 ${adsHistLoading ? "animate-spin" : ""}`} /> {adsHistLoading ? "Bezig…" : "Sync historisch"}
              </Button>
            </div>
          )}

          {/* Gestapeld staafdiagram: omzet-uitsplitsing per dag + transacties */}
          <DagOverzichtChart
            data={dagOverzicht}
            periode={dagPeriode}
            onPeriodeChange={(n) => {
              const clamped = Math.max(7, Math.min(90, isNaN(n) ? 14 : n));
              setDagPeriode(clamped);
              laadDagOverzicht(clamped);
            }}
          />

          {financieel?.ads_fout && (
            <div className="flex items-start gap-2 text-xs p-3 rounded-lg bg-amber-50 border border-amber-200 text-amber-900" data-testid="fin-ads-fout">
              <AlertCircle className="h-4 w-4 shrink-0 mt-0.5" />
              <div>Advertentiekosten konden niet worden opgehaald: <code className="font-mono text-[11px]">{financieel.ads_fout}</code></div>
            </div>
          )}
          {!financieel?.ads_fout && financieel && financieel.ads_kosten_totaal > financieel.ads_kosten_toegewezen + 0.01 && (
            <div className="flex items-start gap-2 text-xs p-3 rounded-lg bg-secondary/50 border border-border text-muted-foreground" data-testid="fin-ads-unassigned">
              <Info className="h-4 w-4 shrink-0 mt-0.5" />
              <div>
                Van de <strong>{fmtEUR(financieel.ads_kosten_totaal)}</strong> advertentiekosten is <strong>{fmtEUR(financieel.ads_kosten_totaal - financieel.ads_kosten_toegewezen)}</strong> niet aan een EAN toegewezen (advertenties op EAN's zonder verkoop of zonder ad-groep-mapping).
              </div>
            </div>
          )}

          {/* Per-EAN marge (echte marge = bruto − commissie − ads − inkoop) */}
          <Sectie titel={`Marge per EAN (top ${Math.min(100, financieel?.per_ean?.length || 0)}) — laagste marge% eerst`}>
            {!financieel?.per_ean?.length ? (
              <div className="p-6 text-sm text-muted-foreground text-center">
                {finLoading ? "Berekenen…" : "Nog geen data. Sync eerst, of vergroot de periode."}
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm min-w-[860px]">
                  <thead className="text-xs uppercase text-muted-foreground bg-secondary/40">
                    <tr>
                      <th className="text-left p-3">Titel / EAN</th>
                      <th className="text-right p-3">Stuks</th>
                      <th className="text-right p-3">Bruto</th>
                      <th className="text-right p-3">Commissie</th>
                      <th className="text-right p-3">Ads</th>
                      <th className="text-right p-3">LVB</th>
                      <th className="text-right p-3">Inkoop</th>
                      <th className="text-right p-3 border-l border-border">Marge €</th>
                      <th className="text-right p-3">Marge %</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {financieel.per_ean.map(r => {
                      const geenIp = !r.heeft_inkoopprijs;
                      return (
                        <tr key={r.ean} data-testid={`fin-ean-${r.ean}`} className={geenIp ? "bg-amber-50/40" : ""}>
                          <td className="p-3">
                            <div className="max-w-sm truncate flex items-center gap-2">
                              {r.titel}
                              {geenIp && (
                                <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 border border-amber-200 shrink-0" title="Geen CRM-koppeling of inkoopprijs ontbreekt">geen inkoop</span>
                              )}
                            </div>
                            <div className="text-xs font-mono text-muted-foreground">{r.ean}</div>
                          </td>
                          <td className="p-3 text-right tabular-nums">{r.stuks}</td>
                          <td className="p-3 text-right tabular-nums">{fmtEUR(r.bruto)}</td>
                          <td className="p-3 text-right tabular-nums text-amber-700">−{fmtEUR(r.commissie)}</td>
                          <td className="p-3 text-right tabular-nums text-rose-700">{r.ads_kosten > 0 ? `−${fmtEUR(r.ads_kosten)}` : "—"}</td>
                          <td className="p-3 text-right tabular-nums text-orange-700">{r.lvb_kosten > 0 ? `−${fmtEUR(r.lvb_kosten)}` : "—"}</td>
                          <td className={`p-3 text-right tabular-nums ${geenIp ? "text-muted-foreground" : "text-slate-700"}`}>
                            {geenIp ? "—" : `−${fmtEUR(r.inkoopprijs_totaal)}`}
                          </td>
                          <td className={`p-3 text-right tabular-nums font-medium border-l border-border ${r.marge < 0 ? "text-rose-700" : "text-emerald-700"}`}>
                            {fmtEUR(r.marge)}
                          </td>
                          <td className={`p-3 text-right tabular-nums ${r.marge_pct < 0 ? "text-rose-700 font-medium" : r.marge_pct < 15 ? "text-amber-700" : "text-emerald-700"}`}>
                            {r.marge_pct}%
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </Sectie>

          {/* Break-even CPC-advies per EAN */}
          <Sectie
            titel="Break-even CPC per EAN — max klik-prijs waarbij je nog break-even draait"
            actie={
              <Button
                size="sm"
                onClick={laadBidPreview}
                disabled={bidBezig || !financieel?.per_ean?.length}
                data-testid="bid-optimize-preview"
                className="bg-primary text-primary-foreground hover:bg-primary/90"
              >
                <RefreshCw className={`h-3.5 w-3.5 mr-1 ${bidBezig ? "animate-spin" : ""}`} />
                Auto-optimaliseer bids
              </Button>
            }
          >
            {!financieel?.per_ean?.length ? (
              <div className="p-6 text-sm text-muted-foreground text-center">
                Geen data. {finPeriode > 30 ? "Sync eerst de historische ads-cache." : ""}
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm min-w-[820px]">
                  <thead className="text-xs uppercase text-muted-foreground bg-secondary/40">
                    <tr>
                      <th className="text-left p-3">Titel / EAN</th>
                      <th className="text-right p-3">Marge / stuk</th>
                      <th className="text-right p-3">CR</th>
                      <th className="text-right p-3">Clicks</th>
                      <th className="text-right p-3">Huidige CPC</th>
                      <th className="text-right p-3 border-l border-border">Break-even CPC</th>
                      <th className="text-right p-3">Advies CPC</th>
                      <th className="text-left p-3">Signaal</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {financieel.per_ean.map(r => {
                      const sig = r.cpc_signaal;
                      const badgeKleur = {
                        stop: "bg-rose-100 text-rose-800 border-rose-200",
                        verlaag: "bg-rose-100 text-rose-800 border-rose-200",
                        krap: "bg-amber-100 text-amber-800 border-amber-200",
                        te_weinig_data: "bg-slate-100 text-slate-700 border-slate-200",
                        ok: "bg-emerald-100 text-emerald-800 border-emerald-200",
                      }[sig] || "";
                      const label = {
                        stop: "STOP — product verliesgevend",
                        verlaag: `Verlaag CPC met ${r.huidige_cpc > 0 ? Math.round((1 - r.advies_cpc / r.huidige_cpc) * 100) : 0}%`,
                        krap: "Krap — binnen 15% marge",
                        te_weinig_data: "Te weinig clicks (<10)",
                        ok: "OK — winstgevend",
                      }[sig] || sig;
                      return (
                        <tr key={r.ean} data-testid={`cpc-ean-${r.ean}`}>
                          <td className="p-3">
                            <div className="max-w-xs truncate">{r.titel}</div>
                            <div className="text-xs font-mono text-muted-foreground">{r.ean}</div>
                          </td>
                          <td className="p-3 text-right tabular-nums">{fmtEUR(r.marge_per_stuk)}</td>
                          <td className="p-3 text-right tabular-nums text-muted-foreground">{(r.conversion_rate * 100).toFixed(1)}%</td>
                          <td className="p-3 text-right tabular-nums text-muted-foreground">{r.ads_clicks}</td>
                          <td className={`p-3 text-right tabular-nums ${sig === "verlaag" ? "text-rose-700 font-medium" : ""}`}>
                            {r.ads_clicks > 0 ? `€ ${r.huidige_cpc.toFixed(2)}` : "—"}
                          </td>
                          <td className="p-3 text-right tabular-nums font-medium border-l border-border">
                            {r.break_even_cpc > 0 ? `€ ${r.break_even_cpc.toFixed(2)}` : "—"}
                          </td>
                          <td className={`p-3 text-right tabular-nums font-medium ${r.advies_cpc > 0 ? "text-emerald-700" : "text-muted-foreground"}`}>
                            {r.advies_cpc > 0 ? `€ ${r.advies_cpc.toFixed(2)}` : "—"}
                          </td>
                          <td className="p-3">
                            <span className={`text-[11px] px-2 py-1 rounded border ${badgeKleur}`}>{label}</span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                <div className="p-3 text-xs text-muted-foreground bg-secondary/20 border-t border-border">
                  <strong>Formule</strong>: Break-even CPC = (marge/stuk zonder ads) × conversieratio. Advies CPC = break-even × 0,85 (15% veiligheidsmarge). Bij <strong>&lt;10 clicks</strong> is de statistiek te wankel voor betrouwbaar advies — draai een langere periode of test met een vaste manual-bid.
                </div>
              </div>
            )}
          </Sectie>

          {/* Bol-facturen */}
          <Sectie titel={`Bol-facturen (${facturen?.facturen?.length || 0})`}>
            {!facturen?.facturen?.length ? (
              <div className="p-6 text-sm text-muted-foreground text-center">
                Nog geen bol-facturen zichtbaar in {facturen?.periode_start || "de gekozen periode"}.
              </div>
            ) : (
              <table className="w-full text-sm">
                <thead className="text-xs uppercase text-muted-foreground bg-secondary/40">
                  <tr>
                    <th className="text-left p-3">Factuurnummer</th>
                    <th className="text-left p-3">Datum</th>
                    <th className="text-left p-3">Type</th>
                    <th className="text-right p-3">Bedrag incl. BTW</th>
                    <th className="text-right p-3 w-24">PDF</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {facturen.facturen.map(f => {
                    const bedrag = f?.legalMonetaryTotal?.taxInclusiveAmount?.amount || 0;
                    const dt = f.issueDate ? new Date(Number(f.issueDate) > 1e12 ? Number(f.issueDate) : Number(f.issueDate) * 1000) : null;
                    return (
                      <tr key={f.invoiceId} data-testid={`fin-factuur-${f.invoiceId}`}>
                        <td className="p-3 font-mono text-xs">{f.invoiceId}</td>
                        <td className="p-3">{dt ? dt.toLocaleDateString("nl-NL") : "—"}</td>
                        <td className="p-3"><Badge variant="outline" className="capitalize text-[10px]">{(f.invoiceType || "").replace(/_/g, " ").toLowerCase()}</Badge></td>
                        <td className={`p-3 text-right font-medium tabular-nums ${bedrag < 0 ? "text-emerald-700" : "text-amber-700"}`}>
                          {fmtEUR(bedrag)}
                          {bedrag < 0 && <div className="text-[10px] text-muted-foreground font-normal">uitbetaald aan jou</div>}
                        </td>
                        <td className="p-3 text-right">
                          <Button variant="ghost" size="icon" onClick={() => downloadFactuur(f.invoiceId)}
                            className="h-8 w-8" data-testid={`fin-factuur-dl-${f.invoiceId}`} title="Download PDF">
                            <Download className="h-4 w-4" />
                          </Button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </Sectie>

          {/* Sales forecast */}
          {forecast && (
            <Sectie titel={`Verkoop-forecast bol (${forecast?.resultaten?.length || 0} EAN's, 13w vooruit)`}>
              {!forecast.resultaten?.length ? (
                <div className="p-6 text-sm text-muted-foreground text-center">
                  Klik "Forecast laden" bovenaan. {forecast.fouten?.length ? `Waarschuwingen: ${forecast.fouten.length}` : ""}
                </div>
              ) : (
                <table className="w-full text-sm">
                  <thead className="text-xs uppercase text-muted-foreground bg-secondary/40">
                    <tr>
                      <th className="text-left p-3">Titel / EAN</th>
                      <th className="text-right p-3">Totaal 13w</th>
                      <th className="text-right p-3">Gem. / week</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {forecast.resultaten.map(r => (
                      <tr key={r.ean} data-testid={`fin-forecast-${r.ean}`}>
                        <td className="p-3">
                          <div className="max-w-sm truncate">{r.titel}</div>
                          <div className="text-xs font-mono text-muted-foreground">{r.ean}</div>
                        </td>
                        <td className="p-3 text-right tabular-nums font-medium">{r.totaal_stuks}</td>
                        <td className="p-3 text-right tabular-nums text-muted-foreground">{r.gemiddeld_per_week}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </Sectie>
          )}

          {/* Performance */}
          {performance && (
            <Sectie titel={`Product-views (30d) — ${performance?.resultaten?.length || 0} EAN's`}>
              {!performance.resultaten?.length ? (
                <div className="p-6 text-sm text-muted-foreground text-center">
                  Klik "Performance laden" bovenaan. {performance.fouten?.length ? `Waarschuwingen: ${performance.fouten.length}` : ""}
                </div>
              ) : (
                <table className="w-full text-sm">
                  <thead className="text-xs uppercase text-muted-foreground bg-secondary/40">
                    <tr>
                      <th className="text-left p-3">Titel / EAN</th>
                      <th className="text-right p-3">Views</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {performance.resultaten.map(r => (
                      <tr key={r.ean} data-testid={`fin-perf-${r.ean}`}>
                        <td className="p-3">
                          <div className="max-w-sm truncate">{r.titel}</div>
                          <div className="text-xs font-mono text-muted-foreground">{r.ean}</div>
                        </td>
                        <td className="p-3 text-right tabular-nums font-medium">{r.totaal}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </Sectie>
          )}

          <div className="flex items-start gap-2 text-xs text-muted-foreground p-3 rounded-lg bg-secondary/30 border border-border/60">
            <Info className="h-4 w-4 shrink-0 mt-0.5" />
            <div>
              <strong>Echte marge</strong> = bruto − commissie − ads − LVB-kosten − inkoopprijs (product × stuks). LVB-kosten (OUTBOUND, PICK_PACK, STOCK, FIRST_MILE) worden per EAN uit de bol-invoice-specificaties gehaald. Advertentiekosten komen live uit de Sponsored Products Reporting API (≤30d) of uit de historische cache (>30d, via "Sync historisch"). Rijen met "geen inkoop"-badge zijn EAN's zonder gekoppeld CRM-product. Klik op het <Download className="inline h-3 w-3" />-icoontje om een bol-factuur (incl. Avb-advertentiefactuur) als PDF te downloaden.
            </div>
          </div>
        </TabsContent>
      </Tabs>

      {/* Bid-optimalisatie preview-dialog */}
      <Dialog open={!!koppelVoor} onOpenChange={(o) => !o && setKoppelVoor(null)}>
        <DialogContent className="max-w-lg" data-testid="koppel-ean-dialog">
          <DialogHeader>
            <DialogTitle>EAN koppelen aan CRM-product</DialogTitle>
            <DialogDescription>
              Kies het CRM-product waaraan deze bol-EAN moet worden gekoppeld. Als de EAN op bol.com is gewijzigd,
              vervang je hiermee de oude koppeling zodat de sync weer werkt.
            </DialogDescription>
          </DialogHeader>
          {koppelVoor && (
            <div className="space-y-3">
              {koppelVoor.bron === "handmatig" ? (
                <div>
                  <Label className="text-xs text-muted-foreground">EAN op bol.com</Label>
                  <Input value={koppelVoor.ean} inputMode="numeric" className="font-mono"
                    onChange={(e) => setKoppelVoor(v => ({ ...v, ean: e.target.value.trim() }))}
                    placeholder="8719...."
                    data-testid="koppel-ean-handmatig-input" />
                  <p className="text-[11px] text-muted-foreground mt-1">
                    Gebruik deze route als je nog geen orders of voorraad hebt voor de nieuwe EAN.
                  </p>
                </div>
              ) : (
                <div className="rounded-md bg-secondary/40 p-3 text-sm space-y-1">
                  <div><span className="text-muted-foreground">Titel:</span> <strong>{koppelVoor.titel || "(geen)"}</strong></div>
                  <div><span className="text-muted-foreground">EAN:</span> <span className="font-mono">{koppelVoor.ean}</span></div>
                </div>
              )}
              <div>
                <Label className="text-xs text-muted-foreground">Kies product</Label>
                <select value={koppelProdId}
                  onChange={(e) => {
                    setKoppelProdId(e.target.value);
                    setKoppelVervang("");
                  }}
                  className="w-full mt-1 px-3 py-2 rounded-md border border-border bg-background text-sm"
                  data-testid="koppel-ean-product">
                  <option value="">— selecteer een product —</option>
                  {prodOpties.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.naam} {p.sku ? `(${p.sku})` : ""}
                    </option>
                  ))}
                </select>
              </div>
              {koppelProdId && (() => {
                const geselect = prodOpties.find(p => p.id === koppelProdId);
                const bestaande = geselect?.bol_koppelingen || [];
                return bestaande.length > 0 ? (
                  <div>
                    <Label className="text-xs text-muted-foreground">Vervang oude EAN op dit product (optioneel)</Label>
                    <select value={koppelVervang}
                      onChange={(e) => setKoppelVervang(e.target.value)}
                      className="w-full mt-1 px-3 py-2 rounded-md border border-border bg-background text-sm font-mono text-xs"
                      data-testid="koppel-ean-vervang">
                      <option value="">— geen vervanging (voeg toe) —</option>
                      {bestaande.map((k, i) => (
                        <option key={`${k.ean || "leeg"}-${i}`} value={k.ean}>
                          {k.ean} ({k.niveau || "individueel"} · {k.aantal_stuks || 1} st.){k.titel ? ` — ${k.titel}` : ""}
                        </option>
                      ))}
                    </select>
                    {koppelVervang && (
                      <p className="text-[11px] text-amber-700 mt-1">
                        De oude EAN <span className="font-mono">{koppelVervang}</span> wordt verwijderd uit dit product en vervangen door <span className="font-mono">{koppelVoor.ean}</span>.
                      </p>
                    )}
                  </div>
                ) : null;
              })()}
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label className="text-xs text-muted-foreground">Niveau</Label>
                  <select value={koppelNiveau}
                    onChange={(e) => setKoppelNiveau(e.target.value)}
                    className="w-full mt-1 px-3 py-2 rounded-md border border-border bg-background text-sm"
                    data-testid="koppel-ean-niveau">
                    <option value="individueel">Individueel (1 stuk)</option>
                    <option value="inner">Inner-pack</option>
                    <option value="outer">Outer-pack</option>
                  </select>
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground">Stuks per pack</Label>
                  <Input type="number" min="1" step="1" value={koppelStuks}
                    onChange={(e) => setKoppelStuks(parseInt(e.target.value || "1", 10))}
                    data-testid="koppel-ean-stuks" />
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={() => setKoppelVoor(null)} disabled={koppelBusy}>Annuleren</Button>
            <Button onClick={doeKoppel}
              disabled={koppelBusy || !koppelProdId || !(koppelVoor?.ean || "").trim()}
              data-testid="koppel-ean-submit">
              {koppelBusy ? "Bezig…" : (koppelVervang ? "Vervang EAN" : "Koppel EAN")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={bidPreviewOpen} onOpenChange={setBidPreviewOpen}>
        <DialogContent className="max-w-4xl w-[95vw] max-h-[90vh] overflow-hidden flex flex-col" data-testid="bid-preview-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <TrendingUp className="h-5 w-5 text-emerald-600" />
              Bid-optimalisatie preview
            </DialogTitle>
            <DialogDescription>
              Bekijk de voorgestelde biedaanpassingen voordat ze naar bol.com gaan.
              Alleen ad-groepen met ≥10 clicks per EAN worden meegenomen.
            </DialogDescription>
          </DialogHeader>

          {bidPreview && (
            <>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <div className="p-3 rounded-lg bg-secondary/40 border border-border">
                  <div className="text-[10px] uppercase text-muted-foreground">Ad-groepen</div>
                  <div className="text-lg font-medium tabular-nums">{bidPreview.aantal_ad_groups_geoptimaliseerd}/{bidPreview.aantal_ad_groups_geanalyseerd}</div>
                </div>
                <div className="p-3 rounded-lg bg-secondary/40 border border-border">
                  <div className="text-[10px] uppercase text-muted-foreground">Bids updaten</div>
                  <div className="text-lg font-medium tabular-nums">{(bidPreview.keywords_update || 0) + (bidPreview.target_products_update || 0)}</div>
                  <div className="text-[10px] text-muted-foreground">{bidPreview.keywords_update} kw · {bidPreview.target_products_update} tp</div>
                </div>
                <div className="p-3 rounded-lg bg-emerald-50 border border-emerald-200">
                  <div className="text-[10px] uppercase text-emerald-800">Besparing / klik</div>
                  <div className="text-lg font-medium tabular-nums text-emerald-800">€ {Math.abs(bidPreview.besparing_per_klik || 0).toFixed(2)}</div>
                </div>
                <div className="p-3 rounded-lg bg-rose-50 border border-rose-200">
                  <div className="text-[10px] uppercase text-rose-800">Verhoging / klik</div>
                  <div className="text-lg font-medium tabular-nums text-rose-800">€ {(bidPreview.verhoging_per_klik || 0).toFixed(2)}</div>
                </div>
              </div>

              <div className="flex-1 overflow-auto border border-border rounded-lg mt-3">
                {!bidPreview.voorstellen?.length ? (
                  <div className="p-6 text-sm text-muted-foreground text-center">
                    {bidPreview.waarschuwing || "Geen wijzigingen voorgesteld — alle bids zijn al binnen advies-CPC."}
                  </div>
                ) : (
                  <table className="w-full text-sm min-w-[560px]">
                    <thead className="sticky top-0 bg-card z-10">
                      <tr className="text-xs uppercase text-muted-foreground bg-secondary/40">
                        <th className="text-left p-2.5">Type</th>
                        <th className="text-left p-2.5">Detail / EAN's</th>
                        <th className="text-right p-2.5">Huidig</th>
                        <th className="text-right p-2.5">Nieuw</th>
                        <th className="text-right p-2.5">Δ</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-border">
                      {bidPreview.voorstellen.map((v, i) => (
                        <tr key={`${v.type}-${v.id}-${i}`} data-testid={`bid-voorstel-${i}`}>
                          <td className="p-2.5">
                            <Badge variant="outline" className="text-[10px]">
                              {v.type === "keyword" ? "keyword" : "product"}
                            </Badge>
                          </td>
                          <td className="p-2.5">
                            {v.type === "keyword" && (
                              <div>
                                <div className="font-medium">{v.keyword}</div>
                                <div className="text-[10px] text-muted-foreground">{v.match_type}</div>
                              </div>
                            )}
                            <div className="text-[10px] font-mono text-muted-foreground">
                              {v.eans?.slice(0, 3).join(", ")}{v.eans?.length > 3 ? ` +${v.eans.length - 3}` : ""}
                            </div>
                          </td>
                          <td className="p-2.5 text-right tabular-nums">€ {v.huidig.toFixed(2)}</td>
                          <td className="p-2.5 text-right tabular-nums font-medium">€ {v.nieuw.toFixed(2)}</td>
                          <td className={`p-2.5 text-right tabular-nums font-medium ${v.verschil < 0 ? "text-emerald-700" : "text-rose-700"}`}>
                            {v.verschil < 0 ? "▼" : "▲"} € {Math.abs(v.verschil).toFixed(2)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>

              {Object.keys(bidPreview.skipped_ad_groups || {}).length > 0 && (
                <details className="text-xs text-muted-foreground mt-2">
                  <summary className="cursor-pointer">{Object.keys(bidPreview.skipped_ad_groups).length} ad-groepen overgeslagen — reden bekijken</summary>
                  <div className="mt-2 p-2 rounded bg-secondary/40 max-h-32 overflow-y-auto">
                    {Object.entries(bidPreview.skipped_ad_groups).map(([agid, reden]) => (
                      <div key={agid} className="py-0.5"><span className="font-mono">{agid}</span>: {reden}</div>
                    ))}
                  </div>
                </details>
              )}
            </>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setBidPreviewOpen(false)} data-testid="bid-preview-annuleer">Annuleren</Button>
            <Button
              onClick={pasBidsToe}
              disabled={bidBezig || !bidPreview?.voorstellen?.length}
              data-testid="bid-preview-toepassen"
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
            >
              {bidBezig ? "Bezig…" : `Toepassen bij bol.com (${(bidPreview?.keywords_update || 0) + (bidPreview?.target_products_update || 0)})`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bevestigings-dialoog voor replenishment */}
      <Dialog open={aanvulOpen} onOpenChange={setAanvulOpen}>
        <DialogContent className="max-w-xl w-[95vw]" data-testid="aanvul-dialog">
          <DialogHeader>
            <DialogTitle>Aanvulling aanmelden bij bol.com</DialogTitle>
            <DialogDescription>
              Je meldt {geselecteerdRegels().length} EAN{geselecteerdRegels().length === 1 ? "" : "'s"} aan met totaal <strong>{totaalStuks} stuks</strong>.
              Kies een vrije referentie, een leverdatum op een werkdag en het aantal dozen/pallets. Na een geaccepteerde
              aanmelding download je hieronder de labels en de paklijst; de stuks worden dan van je eigen warehouse afgeboekt.
            </DialogDescription>
          </DialogHeader>
          <div className="max-h-64 overflow-y-auto border border-border rounded-lg divide-y divide-border">
            {geselecteerdRegels().map((r) => (
              <div key={r.ean} className="p-2.5 text-sm flex items-center justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <div className="truncate">{r.titel}</div>
                  <div className="text-xs font-mono text-muted-foreground">{r.ean}</div>
                </div>
                <div className="font-medium tabular-nums shrink-0">{r.aantal}×</div>
              </div>
            ))}
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div className="sm:col-span-1">
              <Label className="text-[10px] uppercase text-muted-foreground">Referentie</Label>
              <Input value={aanmeldForm.reference} data-testid="aanvul-referentie"
                onChange={(e) => { setAanmeldForm(f => ({ ...f, reference: e.target.value.toUpperCase() })); setRefCheck(null); }}
                onBlur={(e) => checkReferentie(e.target.value)} className="h-9" />
            </div>
            <div>
              <Label className="text-[10px] uppercase text-muted-foreground">Verwachte leverdatum</Label>
              <Input type="date" value={aanmeldForm.expected_delivery_date} data-testid="aanvul-leverdatum"
                onChange={(e) => setAanmeldForm(f => ({ ...f, expected_delivery_date: e.target.value }))}
                className="h-9" />
            </div>
            <div>
              <Label className="text-[10px] uppercase text-muted-foreground">Aantal dozen / pallets</Label>
              <Input type="number" min="1" max="66" value={aanmeldForm.number_of_load_carriers}
                data-testid="aanvul-dozen"
                onChange={(e) => setAanmeldForm(f => ({ ...f, number_of_load_carriers: e.target.value }))}
                className="h-9 text-right" />
            </div>
          </div>
          {refCheck && (
            <div className={`text-xs p-2 rounded border ${refCheck.vrij
              ? "bg-emerald-50 border-emerald-200 text-emerald-900"
              : "bg-red-50 border-red-200 text-red-900"}`} data-testid="aanvul-ref-check">
              {refCheck.vrij
                ? `Referentie ${refCheck.referentie} is vrij${refCheck.aangepast ? " (bol staat alleen hoofdletters en cijfers toe, max 14 tekens)" : ""}.`
                : `Referentie ${refCheck.referentie} bestaat al ${refCheck.bestaat_bij_bol ? "bij bol.com" : "in het CRM"} — kies een andere, bol wijst dubbele referenties af.`}
              {refCheck.fout ? ` Controle bij bol mislukt: ${refCheck.fout}` : ""}
            </div>
          )}
          {laatsteReplenishment?.response && (
            <div className="text-xs p-2 rounded bg-emerald-50 border border-emerald-200 text-emerald-900" data-testid="aanvul-vorige">
              Vorige aanmelding: ref <strong>{laatsteReplenishment.reference}</strong> — {laatsteReplenishment.totaal_stuks} stuks
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setAanvulOpen(false)} data-testid="aanvul-annuleer">Annuleren</Button>
            <Button onClick={bevestigReplenishment} disabled={replenishing || (refCheck && !refCheck.vrij)} data-testid="aanvul-bevestig">
              {replenishing ? "Bezig…" : "Bevestigen en aanmelden"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Overzicht van de LVB-zendingen (replenishments) bij bol.com: status, wat bol ontving,
// labels en paklijst, en voorraad terugboeken als een zending niet is doorgegaan.
const ZENDING_FILTERS = [
  { id: "aandacht", label: "Vraagt aandacht" },
  { id: "onderweg", label: "Onderweg" },
  { id: "afgerond", label: "Afgerond" },
  { id: "alle", label: "Alle" },
];

function zendingGroep(r) {
  if (r.aandacht_nodig) return "aandacht";
  return ["DONE", "CANCELLED", "FINISHED"].includes((r.bol_state || "").toUpperCase()) ? "afgerond" : "onderweg";
}

function ZendingStatus({ r }) {
  if (r.afgewezen) return <Badge variant="destructive" data-testid={`zending-status-${r.reference}`}>Afgewezen door bol</Badge>;
  if (r.in_behandeling) return <Badge variant="outline" data-testid={`zending-status-${r.reference}`}>Nog in behandeling</Badge>;
  const state = (r.bol_state || "").toUpperCase();
  const tekst = { DONE: "Ontvangen bij bol", CANCELLED: "Geannuleerd", ARRIVED: "Aangekomen", ANNOUNCED: "Aangemeld", IN_TRANSIT: "Onderweg" }[state] || state || "Geaccepteerd";
  return <Badge variant="secondary" data-testid={`zending-status-${r.reference}`}>{tekst}</Badge>;
}

function ZendingenTab({ aanmeldingen, bezig, onVerversen, onNaAanpassing }) {
  const [filter, setFilter] = usePersistedState("bol:filter", "alle");
  const [mpBezig, setMpBezig] = useState("");
  const [ttBezig, setTtBezig] = useState("");
  const tel = (id) => id === "alle" ? aanmeldingen.length : aanmeldingen.filter(r => zendingGroep(r) === id).length;
  const rijen = filter === "alle" ? aanmeldingen : aanmeldingen.filter(r => zendingGroep(r) === filter);

  const myparcelLabel = async (r) => {
    setMpBezig(r.reference);
    try {
      const ok = await postDownload(`/bol/replenishments/${r.replenishment_id}/myparcel-label`, {},
        `myparcel-bol-${r.reference}.pdf`);
      if (ok) {
        toast.success("MyParcel-transportlabel aangemaakt en gedownload");
        await onNaAanpassing();
      }
    } finally { setMpBezig(""); }
  };

  const verversTracktrace = async (r) => {
    setTtBezig(r.reference);
    try {
      await api.get(`/bol/replenishments/${r.replenishment_id}/myparcel-tracktrace`);
      await onNaAanpassing();
      toast.success("Track & trace bijgewerkt");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Track & trace ophalen mislukt");
    } finally { setTtBezig(""); }
  };

  const testmail = async () => {
    try {
      const { data } = await api.post("/bol/replenishments/melding-test");
      data.verstuurd
        ? toast.success(`Testmail verstuurd naar ${(data.ontvangers || []).join(", ")}`)
        : toast.error(data.fout || "Geen ontvangers of afzender ingesteld");
    } catch (e) { toast.error(e?.response?.data?.detail || "Testmail mislukt"); }
  };

  const terugboeken = async (r) => {
    try {
      const { data } = await api.post(`/bol/replenishments/${r.id}/terugboeken`);
      toast.success(`${data.teruggeboekt_stuks} stuks teruggeboekt in de warehouse-voorraad`);
      await onNaAanpassing();
    } catch (e) { toast.error(e?.response?.data?.detail || "Terugboeken mislukt"); }
  };

  return (
    <div className="space-y-4" data-testid="bol-zendingen">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="font-display text-2xl font-medium">Zendingen naar bol.com</h2>
          <p className="text-sm text-muted-foreground mt-1 max-w-2xl">
            Elke LVB-aanmelding met de echte status bij bol. Het CRM controleert dit elk uur en mailt je zodra bol
            een zending afwijst of afhandelt; bij een afwijzing gaat de voorraad automatisch terug.
          </p>
        </div>
        <div className="flex gap-2">
          <Button size="sm" variant="ghost" onClick={testmail} data-testid="aanmeldingen-melding-test">
            <Mail className="h-3.5 w-3.5 mr-1" />Testmail
          </Button>
          <Button size="sm" variant="outline" onClick={onVerversen} disabled={bezig} data-testid="aanmeldingen-sync">
            <RefreshCw className={`h-3.5 w-3.5 mr-1 ${bezig ? "animate-spin" : ""}`} />
            {bezig ? "Bijwerken…" : "Status bijwerken"}
          </Button>
        </div>
      </div>

      <div className="flex flex-wrap gap-2" data-testid="zending-filters">
        {ZENDING_FILTERS.map(f => (
          <button key={f.id} onClick={() => setFilter(f.id)} data-testid={`zending-filter-${f.id}`}
            className={`px-3 py-1.5 rounded-full text-xs border transition-colors ${filter === f.id
              ? "bg-primary text-primary-foreground border-primary"
              : "bg-card border-border hover:bg-secondary"}`}>
            {f.label} ({tel(f.id)})
          </button>
        ))}
      </div>

      <div className="space-y-3">
        {rijen.map(r => {
          const stuks = (r.regels || []).length
            ? (r.regels || []).reduce((s, x) => s + (x.quantity || 0), 0)
            : (r.bol_regels || []).reduce((s, x) => s + (x.aangemeld || 0), 0);
          const eans = (r.regels || []).length || (r.bol_regels || []).length;
          return (
            <div key={r.id} className="bg-card border border-border rounded-xl p-4" data-testid={`aanmelding-${r.reference}`}>
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-medium">{r.reference}</span>
                    {r.replenishment_id && <span className="text-xs font-mono text-muted-foreground">bol {r.replenishment_id}</span>}
                    <ZendingStatus r={r} />
                    {r.bron === "bol" && <span className="text-[10px] uppercase tracking-wide text-muted-foreground">buiten het CRM</span>}
                  </div>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-x-6 gap-y-1 mt-3 text-xs">
                    <div>
                      <div className="text-muted-foreground">Aangemeld</div>
                      <div className="font-medium">{String(r.aangemaakt_op || "").slice(0, 10) || "—"}</div>
                    </div>
                    <div>
                      <div className="text-muted-foreground">Leverdatum</div>
                      <div className={`font-medium ${r.leverdatum_verstreken ? "text-amber-700" : ""}`}>
                        {r.expected_delivery_date || "—"}
                      </div>
                    </div>
                    <div>
                      <div className="text-muted-foreground">Inhoud</div>
                      <div className="font-medium">{eans} EAN's · {stuks} stuks</div>
                    </div>
                    <div>
                      <div className="text-muted-foreground">Dozen / pallets</div>
                      <div className="font-medium">{r.number_of_load_carriers || "—"}</div>
                    </div>
                  </div>
                </div>
                {r.replenishment_id && (
                  <div className="flex flex-wrap gap-2 sm:shrink-0 justify-end">
                    <Button size="sm" variant="outline" data-testid={`aanmelding-labels-${r.reference}`}
                      onClick={() => downloadFile(`/bol/replenishments/${r.replenishment_id}/labels?formaat=dymo`, `bol-labels-${r.reference}-dymo.pdf`)}>
                      <Download className="h-3.5 w-3.5 mr-1" />Label (Dymo)
                    </Button>
                    <Button size="sm" variant="ghost" className="px-2" title="Origineel A4-label" data-testid={`aanmelding-labels-a4-${r.reference}`}
                      onClick={() => downloadFile(`/bol/replenishments/${r.replenishment_id}/labels?formaat=a4`, `bol-labels-${r.reference}-a4.pdf`)}>
                      A4
                    </Button>
                    <Button size="sm" variant="outline" disabled={mpBezig === r.reference}
                      title="Maak een MyParcel-transportlabel om de dozen naar bol te sturen (adres komt van het bol-label)"
                      data-testid={`aanmelding-myparcel-${r.reference}`}
                      onClick={() => myparcelLabel(r)}>
                      <Truck className={`h-3.5 w-3.5 mr-1 ${mpBezig === r.reference ? "animate-pulse" : ""}`} />
                      {mpBezig === r.reference ? "Bezig…" : (r.myparcel_label_op ? "MyParcel opnieuw" : "MyParcel-label")}
                    </Button>
                    <Button size="sm" variant="outline" data-testid={`aanmelding-pakbon-${r.reference}`}
                      onClick={() => downloadFile(`/bol/replenishments/${r.replenishment_id}/pakbon`, `bol-paklijst-${r.reference}.pdf`)}>
                      <FileText className="h-3.5 w-3.5 mr-1" />Paklijst
                    </Button>
                  </div>
                )}
              </div>

              {(r.bol_regels || []).length > 0 && (
                <div className="mt-3 pt-3 border-t border-border">
                  <div className="text-[10px] uppercase tracking-wide text-muted-foreground mb-1.5">Ontvangen bij bol</div>
                  <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs">
                    {r.bol_regels.map(l => {
                      const volledig = (l.ontvangen || 0) >= (l.aangemeld || 0);
                      return (
                        <span key={l.ean} className={volledig ? "text-muted-foreground" : "text-amber-700 font-medium"}>
                          <span className="font-mono">{l.ean}</span> {l.ontvangen ?? 0}/{l.aangemeld ?? 0}
                        </span>
                      );
                    })}
                  </div>
                </div>
              )}

              {r.myparcel_label_op && (
                <div className="mt-3 pt-3 border-t border-border" data-testid={`aanmelding-myparcel-tracktrace-${r.reference}`}>
                  <div className="flex items-center justify-between gap-2 mb-1.5">
                    <div className="text-[10px] uppercase tracking-wide text-muted-foreground flex items-center gap-1">
                      <Truck className="h-3 w-3" /> MyParcel track &amp; trace
                      <span className="normal-case text-muted-foreground/70">· label {String(r.myparcel_label_op).slice(0, 10)}</span>
                    </div>
                    <Button size="sm" variant="ghost" className="h-6 px-2 text-xs" disabled={ttBezig === r.reference}
                      onClick={() => verversTracktrace(r)} data-testid={`aanmelding-myparcel-ververs-${r.reference}`}>
                      <RefreshCw className={`h-3 w-3 mr-1 ${ttBezig === r.reference ? "animate-spin" : ""}`} />
                      {ttBezig === r.reference ? "Bezig…" : "Status verversen"}
                    </Button>
                  </div>
                  {(r.myparcel_tracktrace || []).length > 0 ? (
                    <div className="flex flex-col gap-1 text-xs">
                      {r.myparcel_tracktrace.map((t, i) => (
                        <div key={t.barcode || i} className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
                          <span className="text-muted-foreground">Doos {i + 1}:</span>
                          {t.url ? (
                            <a href={t.url} target="_blank" rel="noreferrer"
                              className="font-mono text-teal-700 hover:underline inline-flex items-center gap-1"
                              data-testid={`aanmelding-myparcel-tt-link-${r.reference}-${i}`}>
                              {t.barcode} <ExternalLink className="h-3 w-3" />
                            </a>
                          ) : (
                            <span className="font-mono">{t.barcode}</span>
                          )}
                          {t.status && <span className="text-muted-foreground">· {t.status}</span>}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-xs text-muted-foreground">
                      Barcode nog niet beschikbaar bij MyParcel — klik op “Status verversen”.
                    </div>
                  )}
                </div>
              )}

              {(r.fout || (r.ontbreekt || []).length > 0 || r.leverdatum_verstreken || r.teruggeboekt_op
                || (r.afgeboekt_warnings || []).length > 0 || r.kan_terugboeken) && (
                <div className="mt-3 pt-3 border-t border-border space-y-1.5">
                  {r.fout && <div className="text-xs text-destructive" data-testid={`aanmelding-fout-${r.reference}`}>{r.fout}</div>}
                  {(r.ontbreekt || []).length > 0 && (
                    <div className="text-xs text-amber-700" data-testid={`aanmelding-ontbreekt-${r.reference}`}>
                      Nog niet volledig: {r.ontbreekt.join(", ")} ontbreekt
                    </div>
                  )}
                  {r.leverdatum_verstreken && (
                    <div className="text-xs text-amber-700" data-testid={`aanmelding-verstreken-${r.reference}`}>
                      Leverdatum is verstreken terwijl bol de zending nog niet heeft afgerond
                    </div>
                  )}
                  {(r.afgeboekt_warnings || []).length > 0 && (
                    <div className="text-xs text-amber-700">{r.afgeboekt_warnings.join(" · ")}</div>
                  )}
                  {r.teruggeboekt_op && (
                    <div className="text-xs text-emerald-700" data-testid={`aanmelding-teruggeboekt-${r.reference}`}>
                      {r.teruggeboekt_stuks || 0} stuks teruggeboekt in je eigen voorraad op {String(r.teruggeboekt_op).slice(0, 10)}
                    </div>
                  )}
                  {r.kan_terugboeken && (
                    <Button size="sm" variant="outline" onClick={() => terugboeken(r)}
                      data-testid={`aanmelding-terugboeken-${r.reference}`}>
                      <Warehouse className="h-3.5 w-3.5 mr-1" />{r.afgeboekt_stuks} stuks terugboeken
                    </Button>
                  )}
                </div>
              )}
            </div>
          );
        })}
        {rijen.length === 0 && (
          <p className="bg-card border border-border rounded-xl p-8 text-sm text-muted-foreground text-center">
            {aanmeldingen.length === 0
              ? "Nog geen zendingen aangemeld. Meld een aanvulling aan via het tabblad Aanvullen."
              : "Geen zendingen in deze selectie."}
          </p>
        )}
      </div>
    </div>
  );
}
